import React from 'react';
import {ScrollView, View} from 'react-native';
import {isEmpty, isNil, noop} from 'lodash';

import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import NavigationHeader from '/component/layout/NavigationHeader';
import {BonusesID} from '/constant/TestIDs';
import testProperties from '/util/testing/testProperties';

import {useBonuses} from './Bonuses.hook';
import {styles} from './Bonuses.style';
import {Bonus} from './Bonuses.types';
import {BonusesProps} from './Bonuses.types';
import {PieChart} from './PieChart';
import {Tile} from './Tile';

export const Bonuses = ({
  getBonuses = noop,
  bonuses = {},
  onDateSelectorPress = noop,
  currentScreen = '',
  lastPaidRank = '',
}: BonusesProps) => {
  const {
    tt,
    totalBonus,
    onTilePress,
    formattedPeriods,
    currentBonuses,
    selectedPeriodIndex,
    externalMutations,
    selectedTileIndex,
    onSelectorPress,
    sortedColorScale,
  } = useBonuses({
    getBonuses,
    bonuses: bonuses as Record<string, Bonus[]> | undefined,
    onDateSelectorPress,
    currentScreen,
    lastPaidRank,
  });
  const getTile = (
    {bonusAmount, bonusDescription, currencyCode}: Bonus,
    index: any,
  ) => (
    <Tile
      key={bonusDescription}
      description={bonusDescription}
      amount={bonusAmount}
      currentIndex={index}
      onPress={onTilePress}
      currencyCode={currencyCode}
      selectedIndex={selectedTileIndex}
      colorScale={sortedColorScale}
    />
  );

  return (
    <NavigationHeader
      style={{base: styles.base, title: styles.title}}
      testID={BonusesID.bonusesPage}
      title={tt('bonuses', 'Bonuses')}>
      {isNil(currentBonuses) ? (
        <View style={styles.loading}>
          <LoadingIndicator size="large" />
        </View>
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}>
          <View style={styles.contentContainer}>
            <TouchableOpacity
              style={styles.datePicker}
              onPress={onSelectorPress}
              {...testProperties(BonusesID.bonusesDatePickerButton, true)}>
              <Text
                variant="button"
                testID={BonusesID.bonusesDatePickerSelectedDate}>
                {formattedPeriods[selectedPeriodIndex]}
              </Text>
              <Icon name="chevron-down" style={styles.chevronDown} />
            </TouchableOpacity>
          </View>
          {isEmpty(currentBonuses) ? (
            <View style={styles.emptyScreen}>
              <Text variant="large">{tt('noBonuses', 'No Bonuses')}</Text>
            </View>
          ) : (
            <Gutter style={styles.totalBonusesContainer}>
              <View style={styles.totalBonusContainer}>
                <Text
                  variant="body"
                  style={styles.totalBonusTitle}
                  testID={BonusesID.bonusesTotalLabel}>
                  {tt('totalBonuses', 'Total bonuses')}
                </Text>
                <InlineFormattedNumber
                  style={styles.totalBonus}
                  textVariant="large"
                  value={totalBonus}
                  decimalPlaces={2}
                  testID={BonusesID.bonusesTotalValue}
                  currencyCode={currentBonuses[0]?.currencyCode}
                />
              </View>
              <View style={styles.splitter} />
              <PieChart
                data={currentBonuses}
                onPress={onTilePress}
                totalBonus={totalBonus}
                selectedTileIndex={selectedTileIndex}
                externalMutations={externalMutations}
                colorScale={sortedColorScale}
              />
              <View>{currentBonuses.map(getTile)}</View>
            </Gutter>
          )}
        </ScrollView>
      )}
    </NavigationHeader>
  );
};
